import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { parseOllieMarkdown } from 'src/utils/ollieMarkdown'

const INTERNAL_PATH_REGEX = /(\/v\/[a-zA-Z0-9]+\/[a-zA-Z0-9_\-/]+)/g

const PATH_LABELS = [
  { pattern: /\/purchasing\/gross-profit$/, label: 'Gross Profit' },
  { pattern: /\/purchasing\/receipting$/, label: 'Goods receipting' },
  { pattern: /\/purchasing\/invoices$/, label: 'Invoices & Credit Notes' },
  { pattern: /\/purchasing\/orders$/, label: 'Orders' },
  { pattern: /\/purchasing\/basket$/, label: 'Basket' },
  { pattern: /\/checkout$/, label: 'Checkout' },
  { pattern: /\/integrations$/, label: 'Integrations' },
  { pattern: /\/settings$/, label: 'Settings' },
  { pattern: /\/subscriptions/, label: 'Subscriptions' },
  { pattern: /\/menu-builder$/, label: 'Menu Builder' },
  { pattern: /\/stockTakes$/, label: 'Stock takes' },
  { pattern: /\/suppliers$/, label: 'Your suppliers' },
  { pattern: /\/products$/, label: 'Shopping lists' },
  { pattern: /\/catalogue$/, label: 'Catalogue' },
  { pattern: /\/special-purchases$/, label: 'Special Purchases' },
  { pattern: /\/purchasing\/dataplus$/, label: 'Dataplus' },
]

export function getPathLabel(path) {
  const match = PATH_LABELS.find(item => item.pattern.test(path))
  return match ? match.label : 'Open in OMS'
}

export function splitMessageContent(content) {
  if (!content) {
    return []
  }

  const parts = []
  let lastIndex = 0
  let match

  const regex = new RegExp(INTERNAL_PATH_REGEX.source, 'g')
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'path', value: match[1] })
    lastIndex = match.index + match[1].length
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) })
  }

  return parts.length ? parts : [{ type: 'text', value: content }]
}

export function OllieMessageContent({ content }) {
  const parts = splitMessageContent(content)

  return (
    <>
      {parts.map((part, index) => {
        if (part.type === 'path') {
          return (
            <PathLink key={`${part.value}-${index}`} to={part.value}>
              {getPathLabel(part.value)} →
            </PathLink>
          )
        }
        return <React.Fragment key={index}>{part.value}</React.Fragment>
      })}
    </>
  )
}

const renderInline = (tokens, { links }) =>
  tokens.map((token, index) => {
    if (token.type === 'bold') {
      return (
        <strong key={index}>
          {token.children
            ? renderInline(token.children, { links })
            : token.value}
        </strong>
      )
    }
    if (token.type === 'italic') return <em key={index}>{token.value}</em>
    return links ? (
      <OllieMessageContent key={index} content={token.value} />
    ) : (
      <React.Fragment key={index}>{token.value}</React.Fragment>
    )
  })

/**
 * An Ollie reply with its bold, italics and lists drawn properly. While the
 * typewriter is revealing (`partial`), paths stay text and unclosed markers
 * are hidden.
 */
export function OllieRichContent({
  content,
  partial = false,
  trailing = null,
}) {
  const blocks = parseOllieMarkdown(content, { partial })
  const links = !partial
  const lastBlock = blocks.length - 1

  return (
    <RichBody data-testid='ollie-rich-content'>
      {!blocks.length && trailing}
      {blocks.map((block, index) => {
        if (block.type === 'list') {
          const List = block.ordered ? 'ol' : 'ul'
          return (
            <List
              key={index}
              start={
                block.ordered && block.start !== 1 ? block.start : undefined
              }
            >
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  {renderInline(item, { links })}
                  {index === lastBlock &&
                    itemIndex === block.items.length - 1 &&
                    trailing}
                </li>
              ))}
            </List>
          )
        }
        return (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, { links })}
              </React.Fragment>
            ))}
            {index === lastBlock && trailing}
          </p>
        )
      })}
    </RichBody>
  )
}

const RichBody = styled.div`
  white-space: normal;

  p,
  ul,
  ol {
    margin: 0 0 8px;
  }

  p:last-child,
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    padding-left: 20px;
  }

  li + li {
    margin-top: 2px;
  }

  strong {
    font-weight: 600;
  }
`

const PathLink = styled(Link)`
  display: inline-block;
  margin: 4px 0;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colours.bgGrey};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    background: ${({ theme }) => theme.colours.mayoWhite};
  }
`
